import React, { useState, useEffect, useContext } from "react";
import CodeIcon from '@material-ui/icons/Code';
import Surface from "../components/Surface";
import translate from "../i18n/Translator";
import { RouterParams } from "../router/RouterParams";
import { AppContext } from "../context/AppContext";
import GridDx from "../components/GridDx";
import { ApiLog, ApiLogs, ApiLogsListParams, METHODS } from "../api/ApiLog";
import queryString from "query-string";
import { initialPageSize, initialPageZero } from "../components/Pagination";
import { DataTypeProvider, DataTypeProviderProps, Filter} from "@devexpress/dx-react-grid";
import DateFormat, { formatDateString } from "../components/DateFormat";
import { Redirect, useHistory } from "react-router-dom";
import { getApiLogs as getTenantApiLogs } from "../api/ApiLogApi";
import { ErrorSnackbar } from "../components/Snackbars";
import { getApiLogs } from "../api/SupportAPI";
import { Box, Divider, FormControl, Grid, IconButton, Select } from "@material-ui/core";
import RefreshTwoToneIcon from '@material-ui/icons/RefreshTwoTone';


export default function ApiLogView({ match }: RouterParams) {
    const context = useContext(AppContext);
    const isTenant = context.session!.role.id === "owner";
    const [data, setData] = useState<ApiLogs>();
    const qs = queryString.parse(window.location.search);
    const paramsFromQueryString = (): ApiLogsListParams => {
        return {
            "date": typeof qs["date"] === "string" ? qs["date"] as string : "",
            "id": typeof qs["id"] === "string" ? qs["id"] as string : "",
            "tenant": typeof qs["tenant"] === "string" ? qs["tenant"] as string : "",
            "method": typeof qs["method"] === "string" ? qs["method"] : "",
            "url": typeof qs["url"] === "string" ? qs["url"] : "",
            "ip_address": typeof qs["ip_address"] === "string" ? qs["ip_address"] : "",
            "response_code": typeof qs["response_code"] === "string" ? qs["response_code"] : "",
        } as ApiLogsListParams;
    };

    const [params, setParams] = useState<ApiLogsListParams>(paramsFromQueryString);
    const [page, setPage] = useState<number>(initialPageZero);
    const [pageSize, setPageSize] = useState<number>(initialPageSize);
    const history = useHistory();
    const [status, setStatus] = useState<string>("loading");
    const [error, setError] = useState<string>();
    const [redirect, setRedirect] = useState<string | undefined>();

    const paramsToFilters = (): Filter[] => {
        let filters = [
            { columnName: 'id', value: params.id },
            { columnName: 'date', value: params.date },
            { columnName: 'method', value: params.method },
            { columnName: 'url', value: params.url },
            { columnName: 'response_code', value: params.response_code },
            { columnName: 'ip_address', value: params.ip_address },
        ] as Filter[];
        if(!isTenant){
            filters.push(
                { columnName: 'tenant', value: params.tenant }
            );
        }
        return filters;
    };
    const [filters, setFilters] = useState<Filter[]>(paramsToFilters);

    const load = () => {
        setStatus("loading");
        pushHistory();

        if(isTenant){
            getTenantApiLogs(context.session!.tenant!.id, pageSize, pageSize * page, params ).then((response) => {
                setData(response);
            }).catch((error) => {
                setError(error.message);
            }).finally(() => {
                setStatus("loaded");
            });
        } else {
            getApiLogs(pageSize, pageSize * page, params).then((response) => {
                setData(response);
            }).catch((error) => {
                setError(error.message);
            }).finally(() => {
                setStatus("loaded");
            });
        }
    }

    useEffect(load, [context.session, page, pageSize, params, filters]);

    const columns = [
        {
            name: 'id',
            title: translate('api_logs.columns.id') as string
        },
        {
            name: 'tenant',
            title: translate('api_logs.columns.tenant') as string,
            getCellValue: (row: any) => row.tenant_name
        },
        {
            name: 'method',
            title: translate('api_logs.columns.method') as string
        },
        {
            name: 'url',
            title: translate('api_logs.columns.url') as string
        },
        {
            name: 'date',
            title: translate('api_logs.columns.date') as string
        },
        {
            name: 'response_code',
            title: translate('api_logs.columns.response_code') as string
        },
        {
            name: 'ip_address',
            title: translate('api_logs.columns.ip_address') as string,
            getCellValue: (row: any) => row.ip
        }
    ];

    const columnsTenant = [
        {
            name: 'id',
            title: translate('api_logs.columns.id') as string
        },
        {
            name: 'method',
            title: translate('api_logs.columns.method') as string
        },
        {
            name: 'url',
            title: translate('api_logs.columns.url') as string
        },
        {
            name: 'date',
            title: translate('api_logs.columns.date') as string
            
        },
        {
            name: 'response_code',
            title: translate('api_logs.columns.response_code') as string
        },
        {
            name: 'ip_address',
            title: translate('api_logs.columns.ip_address') as string,
            getCellValue: (row: any) => row.ip
        }
    ];

    const [columnsFormat] = useState([
        { columnName: 'id', width: 300},
        { columnName: 'method', width: 115 },
        { columnName: 'url'},
        { columnName: 'date', wordWrapEnabled: true, width: 200 },
        { columnName: 'response_code', width: 140 },
        { columnName: 'ip_address', width: 200},
    ]) as any;

    const dateColumns = ['date'];

    const textColumns = ['id', 'tenant', 'response_code', 'ip_address', 'url'];

    const MethodFilterEditor = (params: DataTypeProvider.ValueEditorProps) => {
        const onChange = (event: any) => {
            const { value: targetValue } = event.target;
            if (targetValue.trim() === '') {
                params.onValueChange("");
                return;
            }
            params.onValueChange(targetValue);
        };

        return (
            <Box px={1}>
                <FormControl >
                    <Select native style={{
                        fontSize: "10px"
                    }}
                        labelId="is_default"
                        id="is_default"
                        value={params.value ? params.value : ''}
                        onChange={onChange} >
                        <option key={"ALL"} value={""}>{""}</option>
                        {METHODS.map((value) => {
                            return <option key={value} value={value}>{value}</option>
                        })}
                    </Select>
                </FormControl>
            </Box>
        );
    };

    const NormalTypeProvider = (props: DataTypeProviderProps) => (
        <DataTypeProvider {...props} />
    );

    const customPlugins = [
        <NormalTypeProvider for={['method']} editorComponent={MethodFilterEditor} />,
    ];

    const onChangedPage = (page: number) => {
        setPage(page);
    };

    const onChangedPageSize = (pageSize: number) => {
        setPageSize(pageSize);
    };

    const setFiltersHandler = (filters: Filter[]) => {
        setFilters(filters);
        setParamsFromfilters(filters);
        setPage(0);
    };

    const setParamsFromfilters = (filters: Filter[]) => {
        let temp = params;
        filters.forEach(filter => {
            if (filter.value !== undefined) {
                switch (filter.columnName) {
                    case "date":
                        let datesS = filter.value.split(" - ");
                        if (datesS.length === 2) {
                            temp.date = formatDateString(datesS[0]) + " - " + formatDateString(datesS[1]);
                        } else {
                            temp.date = "";
                        }
                        break;
                    case "id":
                        temp.id = filter.value;
                        break;
                    case "method":
                        temp.method = filter.value;
                        break;
                    case "response_code":
                        temp.response_code = filter.value;
                        break;
                    case "tenant":
                        temp.tenant = filter.value;
                        break;
                    case "ip_address":
                        temp.ip_address = filter.value;
                        break;
                    case "url":
                        temp.url = filter.value;
                        break;
                    default: break;
                }
            }
        });
        setParams(temp);
    };

    const onClickedRow = (apiLog: ApiLog) => {
        pushHistory();
        setRedirect(`/api-logs/${apiLog.id}/detail`);
    };

    if (redirect) {
        return (<Redirect to={redirect} />);
    }

    const pushHistory = () => {
        let qs = queryString.parse(window.location.search);
        qs["date"] = params.date || "";
        qs["id"] = params.id || "";
        qs["tenant"] = params.tenant || "";
        qs["method"] = params.method || "";
        qs["url"] = params.url || "";
        qs["ip_address"] = params.ip_address || "";
        qs["response_code"] = params.response_code || "";
        qs["page"] = "0";

        let url = window.location.pathname + "?" + queryString.stringify(qs);
        history.push(url);
    };

    const clickRowColumns = ['id'];

    const onCloseSnackbars = () => {
        setError(undefined);
    };

    return (
        <Surface title={translate(`api_logs.title.${isTenant ? "tenant": "support"}`) as string}
            icon={<CodeIcon />} 
            className="PaperPagination" >
                <Grid container alignItems="center" justify="flex-end" className="TableFilter" spacing={1}>
                <Grid item xs="auto">
                    <IconButton color="primary" size="small" onClick={load} disabled={status === "loading"}  >
                        <RefreshTwoToneIcon />
                    </IconButton>
                </Grid>
            </Grid>
            <Divider />
                <GridDx
                loading={status === "loading"}
                rows={data ? data.items : []}
                page={page}
                pageSize={pageSize}
                totalRows={data ? data.total : 0}
                columns={isTenant ? columnsTenant : columns}
                columnsFormat={columnsFormat}
                dateColumns={dateColumns}
                dateFormat="DD/MM/YYYY hh:mm:ss"
                textColumns={textColumns}
                filters={filters}
                customFormatColumns={customPlugins}
                onChangedPage={onChangedPage}
                onChangedPageSize={onChangedPageSize}
                setFiltersHandler={setFiltersHandler}
                onClickRow={onClickedRow}
                clickRowColumns={clickRowColumns}
            />
            <ErrorSnackbar message={error} onClose={onCloseSnackbars} />
            </Surface>
    );
}